'use strict';

var gridDateFilterUtils = require('../gridDateFilterUtils');

function isEmptyFilterEntry(value) {
    if (value == null) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object' && value.filter !== undefined) {
        return value.filter === null || value.filter === undefined || value.filter === '';
    }
    return false;
}

function isInvalidDateFilterEntry(value) {
    return gridDateFilterUtils.isInvalidDateFilterEntry(value);
}

function sanitizeFilterModel(filterModel) {
    if (!filterModel || typeof filterModel !== 'object') return {};
    const result = {};
    Object.keys(filterModel).forEach((key) => {
        const entry = filterModel[key];
        if (isEmptyFilterEntry(entry) || isInvalidDateFilterEntry(entry)) return;
        result[key] = entry;
    });
    return result;
}

function hasActiveFilterModel(filterModel) {
    return Object.keys(sanitizeFilterModel(filterModel)).length > 0;
}

function findColumnDefByKey(columnDefs, colKey) {
    if (!columnDefs || !colKey) return null;
    return columnDefs.find(c => c.colId === colKey || c.field === colKey) || null;
}

function setFilterModelsEqual(a, b) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v) => b.indexOf(v) >= 0);
}

function mergeSavedValuesIntoSetFilterColumns(columnDefs, filterModel) {
    const model = sanitizeFilterModel(filterModel);
    if (!columnDefs || !hasActiveFilterModel(model)) return;
    Object.keys(model).forEach((colKey) => {
        const saved = model[colKey];
        if (!Array.isArray(saved)) return;
        const col = findColumnDefByKey(columnDefs, colKey);
        if (!col || col.filter !== 'set') return;
        if (!col.filterParams) col.filterParams = {};
        if (!Array.isArray(col.filterParams.values)) col.filterParams.values = [];
        saved.forEach((v) => {
            if (v != null && col.filterParams.values.indexOf(v) < 0) {
                col.filterParams.values.push(v);
            }
        });
    });
}

function applySetFilterModel(filterApi, values) {
    if (!filterApi || !Array.isArray(values)) return;
    if (values.length === 0) {
        if (filterApi.selectNothing) {
            filterApi.selectNothing();
        } else if (typeof filterApi.setModel === 'function') {
            filterApi.setModel([]);
        }
        return;
    }
    if (filterApi.selectNothing && filterApi.selectValue) {
        filterApi.selectNothing();
        values.forEach((v) => filterApi.selectValue(v));
    } else if (typeof filterApi.setModel === 'function') {
        filterApi.setModel(values);
    }
}

function parseGridLocalStorageState(localStorageKey) {
    try {
        const raw = window.localStorage && window.localStorage[localStorageKey];
        if (!raw) return { state: null, filterModel: null };

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return { state: null, filterModel: null };

        if (Array.isArray(parsed)) {
            return { state: parsed, filterModel: null };
        }
        if (parsed.state != null && typeof parsed.state === 'object') {
            const storedFilter = parsed.hasOwnProperty('filterModel') ? parsed.filterModel : null;
            return {
                state: parsed.state,
                filterModel: hasActiveFilterModel(storedFilter) ? sanitizeFilterModel(storedFilter) : null
            };
        }
        return { state: parsed, filterModel: null };
    } catch (e) {
        return { state: null, filterModel: null };
    }
}

function saveGridStateToLocalStorage(localStorageKey, columnState, filterModel) {
    try {
        if (!window.localStorage || !columnState) return;
        const data = {
            state: columnState,
            updatedAt: new Date().toISOString()
        };
        if (filterModel !== undefined && hasActiveFilterModel(filterModel)) {
            data.filterModel = filterModel;
        }
        window.localStorage[localStorageKey] = JSON.stringify(data);
    } catch (e) { }
}

/**
 * Persistencia de filtros/columnas de ag-Grid 3 para dashboard-data (server-side, apply:true en sets).
 */
function createDataGridFilterPersistence(options) {
    const state = {
        activeFilterModel: {},
        savedFilterModelToRestore: null,
        filtersRestored: false,
        restoreInProgress: false,
        reapplying: false,
        shouldReapplyDropped: false,
        lastPersistedKey: null
    };

    const getGridOptions = () => options.getGridOptions();
    const getApi = () => {
        const grid = getGridOptions();
        return grid && grid.api;
    };
    const getColumnDefs = () => {
        const grid = getGridOptions();
        return grid && grid.columnDefs;
    };
    const getColumnApi = () => {
        const grid = getGridOptions();
        return grid && grid.columnApi;
    };

    const isSyncBlocked = () => {
        return options.getGridRestoringPreferences() || state.restoreInProgress || state.reapplying;
    };

    const filterEntriesEqual = (exp, act) => {
        if (exp === act) return true;
        if (exp == null || act == null) return false;
        if (Array.isArray(exp) && Array.isArray(act)) {
            return exp.length === act.length && exp.every((v, i) => v === act[i]);
        }
        if (typeof exp === 'object' && typeof act === 'object') {
            return exp.type === act.type && String(exp.filter) === String(act.filter);
        }
        return false;
    };

    const verifyFiltersRestored = (expected, current) => {
        if (!hasActiveFilterModel(expected)) return true;
        current = current || {};
        const columnDefs = getColumnDefs();
        return Object.keys(expected).every((colKey) => {
            const exp = expected[colKey];
            const col = findColumnDefByKey(columnDefs, colKey);
            if (col && col.filter === 'set') {
                if (!Array.isArray(exp)) return true;
                const act = current[colKey];
                return act && Array.isArray(act) && exp.length === act.length &&
                    exp.every((v) => act.indexOf(v) >= 0);
            }
            return filterEntriesEqual(exp, current[colKey]);
        });
    };

    const applyNonSetFiltersFromModel = (api, columnDefs, fm) => {
        const modelToSet = {};
        Object.keys(fm).forEach((colKey) => {
            const saved = fm[colKey];
            const col = findColumnDefByKey(columnDefs, colKey);
            if (col && col.filter === 'set') return;
            if (isEmptyFilterEntry(saved) || isInvalidDateFilterEntry(saved)) return;
            modelToSet[colKey] = saved;
        });
        if (Object.keys(modelToSet).length === 0 || !api.setFilterModel) return;
        const current = (api.getFilterModel && api.getFilterModel()) || {};
        api.setFilterModel(Object.assign({}, current, modelToSet));
    };

    const buildFilterModelFromGrid = (paramsFilterModel) => {
        const merged = Object.assign({}, sanitizeFilterModel(paramsFilterModel || {}));
        const activeWithChanges = [];
        const setInactiveWithActive = [];
        const setColumnKeys = [];

        const api = getApi();
        const columnDefs = getColumnDefs();
        if (!api || !columnDefs) {
            return sanitizeFilterModel(merged);
        }

        const rawGridFm = api.getFilterModel && api.getFilterModel() || {};

        columnDefs.forEach((col) => {
            const key = col.colId || col.field;
            if (!key || col.filter !== 'set') return;
            setColumnKeys.push(key);

            try {
                const filterApi = api.getFilterApi(key);
                if (!filterApi) return;
                const isActive = filterApi.isFilterActive && filterApi.isFilterActive();
                const model = filterApi.getModel && filterApi.getModel();
                const prev = state.activeFilterModel[key];

                if (isActive && model && model.length > 0) {
                    if (prev != null && !setFilterModelsEqual(model, prev)) {
                        activeWithChanges.push(key);
                    }
                    merged[key] = model;
                } else if (isActive) {
                    // Aplicado sin ninguna opción marcada: filtro activo vacío (0 filas, como en graph)
                    merged[key] = [];
                } else if (prev != null) {
                    setInactiveWithActive.push(key);
                }
            } catch (e) { }
        });

        const preserveDroppedSetFilters = activeWithChanges.length > 0 && setInactiveWithActive.length > 0;
        state.shouldReapplyDropped = preserveDroppedSetFilters;

        setInactiveWithActive.forEach((key) => {
            if (preserveDroppedSetFilters && state.activeFilterModel[key] != null) {
                merged[key] = state.activeFilterModel[key];
            } else {
                delete merged[key];
            }
        });

        Object.keys(rawGridFm).forEach((key) => {
            if (setColumnKeys.indexOf(key) >= 0) return;
            if (isEmptyFilterEntry(rawGridFm[key]) || isInvalidDateFilterEntry(rawGridFm[key])) {
                delete merged[key];
            } else {
                merged[key] = rawGridFm[key];
            }
        });

        // No sanitizar aquí: member:[] es válido en sesión; al persistir se omite (como graph)
        return merged;
    };

    const syncFromGrid = (paramsFilterModel) => {
        state.activeFilterModel = buildFilterModelFromGrid(paramsFilterModel);
        return state.activeFilterModel;
    };

    const pruneSavedRestoreModelFromActive = () => {
        if (!state.savedFilterModelToRestore) return;
        Object.keys(state.savedFilterModelToRestore).forEach((key) => {
            if (!Object.prototype.hasOwnProperty.call(state.activeFilterModel, key)) {
                delete state.savedFilterModelToRestore[key];
            }
        });
        if (!hasActiveFilterModel(state.savedFilterModelToRestore)) {
            state.savedFilterModelToRestore = null;
            state.filtersRestored = true;
        }
    };

    const getEffectiveForRequest = () => {
        if (!state.restoreInProgress && !state.reapplying) {
            pruneSavedRestoreModelFromActive();
        }
        let merged = Object.assign({}, state.activeFilterModel);
        if (state.savedFilterModelToRestore && !state.filtersRestored &&
            Object.keys(sanitizeFilterModel(merged)).length === 0) {
            merged = Object.assign({}, state.savedFilterModelToRestore);
        }
        return merged;
    };

    const reapplyDroppedSetFilters = () => {
        const api = getApi();
        const columnDefs = getColumnDefs();
        if (state.reapplying || state.restoreInProgress || !hasActiveFilterModel(state.activeFilterModel) ||
            !api || !columnDefs) {
            return;
        }

        const toReapply = [];
        Object.keys(state.activeFilterModel).forEach((colKey) => {
            const saved = state.activeFilterModel[colKey];
            if (!Array.isArray(saved) || saved.length === 0) return;
            const col = findColumnDefByKey(columnDefs, colKey);
            if (!col || col.filter !== 'set') return;
            try {
                const filterApi = api.getFilterApi(colKey);
                if (filterApi && filterApi.isFilterActive && !filterApi.isFilterActive()) {
                    toReapply.push(colKey);
                }
            } catch (e) { }
        });

        if (toReapply.length === 0) return;

        state.reapplying = true;
        options.setGridRestoringPreferences(true);

        try {
            toReapply.forEach((colKey) => {
                const values = state.activeFilterModel[colKey];
                const partialModel = {};
                partialModel[colKey] = values;
                mergeSavedValuesIntoSetFilterColumns(columnDefs, partialModel);
                applySetFilterModel(api.getFilterApi(colKey), values);
            });
            if (api.onFilterChanged) {
                api.onFilterChanged();
            }
        } catch (e) {
            console.error('Error al reaplicar filtros:', e);
        }

        options.$timeout(() => {
            state.reapplying = false;
            options.setGridRestoringPreferences(false);
            if (canPersist()) {
                state.activeFilterModel = buildFilterModelFromGrid(api.getFilterModel && api.getFilterModel());
                persistToLocalStorage();
            }
        }, 0);
    };

    const restoreFromStorage = () => {
        if (state.restoreInProgress || state.filtersRestored || options.hasTaskId() ||
            !hasActiveFilterModel(state.savedFilterModelToRestore) || !getApi() || !getColumnDefs()) {
            return;
        }

        state.restoreInProgress = true;
        const fm = sanitizeFilterModel(state.savedFilterModelToRestore);
        if (!hasActiveFilterModel(fm)) {
            state.filtersRestored = true;
            state.savedFilterModelToRestore = null;
            state.restoreInProgress = false;
            return;
        }

        options.setGridRestoringPreferences(true);
        const columnDefs = getColumnDefs();
        mergeSavedValuesIntoSetFilterColumns(columnDefs, fm);

        try {
            const api = getApi();
            Object.keys(fm).forEach((colKey) => {
                const col = findColumnDefByKey(columnDefs, colKey);
                if (!col || col.filter !== 'set' || !Array.isArray(fm[colKey])) return;
                try {
                    applySetFilterModel(api.getFilterApi(colKey), fm[colKey]);
                } catch (e) { }
            });

            applyNonSetFiltersFromModel(api, columnDefs, fm);

            if (api.onFilterChanged) {
                api.onFilterChanged();
            }

            const current = sanitizeFilterModel(api.getFilterModel && api.getFilterModel());
            const restored = verifyFiltersRestored(fm, current);
            state.activeFilterModel = Object.assign({}, fm);
            state.lastPersistedKey = JSON.stringify(fm);
            if (restored) {
                state.filtersRestored = true;
                state.savedFilterModelToRestore = null;
            }

            options.$timeout(() => {
                options.setGridRestoringPreferences(false);
                state.restoreInProgress = false;
                if (hasActiveFilterModel(fm)) {
                    options.refreshData();
                }
            }, 200);
        } catch (e) {
            console.error('Error al restaurar filtros:', e);
            options.$timeout(() => {
                options.setGridRestoringPreferences(false);
                state.restoreInProgress = false;
            }, 200);
        }
    };

    const getLocalStorageKey = () => 'dataGridFormId'.concat(options.getFormId());

    const getPreferencesToRestore = () => {
        const localData = parseGridLocalStorageState(getLocalStorageKey());
        if (localData.state) {
            return {
                state: localData.state,
                filterModel: hasActiveFilterModel(localData.filterModel) ?
                    sanitizeFilterModel(localData.filterModel) : null
            };
        }
        return { state: null, filterModel: null };
    };

    const canPersist = () => {
        if (options.hasTaskId() || !options.getFormId() || !getApi() || !getColumnApi() ||
            options.getGridRestoringPreferences()) {
            return false;
        }
        if (state.savedFilterModelToRestore && !state.filtersRestored) {
            const gridFm = getApi().getFilterModel && getApi().getFilterModel();
            if (!hasActiveFilterModel(gridFm)) {
                return false;
            }
        }
        return true;
    };

    const persistFromParams = (params) => {
        if (!params || !options.getGridInitState() || options.hasTaskId() ||
            options.getGridRestoringPreferences() || !options.getFormId() || !getColumnApi()) {
            return;
        }
        if (state.savedFilterModelToRestore && !state.filtersRestored) {
            const gridFm = getApi().getFilterModel && getApi().getFilterModel();
            if (!hasActiveFilterModel(gridFm)) {
                return;
            }
        }

        const fm = sanitizeFilterModel(params.filterModel || {});
        const localStorageKey = getLocalStorageKey();
        const columnState = getColumnApi().getState();

        if (!hasActiveFilterModel(fm)) {
            state.activeFilterModel = {};
            state.lastPersistedKey = JSON.stringify({});
            saveGridStateToLocalStorage(localStorageKey, columnState, null);
            return;
        }

        state.activeFilterModel = Object.assign({}, fm);
        const fmKey = JSON.stringify(fm);
        if (fmKey === state.lastPersistedKey) return;
        state.lastPersistedKey = fmKey;
        saveGridStateToLocalStorage(localStorageKey, columnState, fm);
    };

    const persistToLocalStorage = () => {
        if (!canPersist()) return;
        const fm = sanitizeFilterModel(syncFromGrid(getApi().getFilterModel && getApi().getFilterModel()));
        state.activeFilterModel = fm;
        state.lastPersistedKey = JSON.stringify(fm);
        saveGridStateToLocalStorage(getLocalStorageKey(), getColumnApi().getState(),
            hasActiveFilterModel(fm) ? fm : null);
    };

    const loadPendingFilters = (filterModel) => {
        if (!hasActiveFilterModel(filterModel)) return;
        const storedFm = sanitizeFilterModel(filterModel);
        state.activeFilterModel = Object.assign({}, storedFm);
        state.savedFilterModelToRestore = storedFm;
        state.lastPersistedKey = JSON.stringify(storedFm);
    };

    const onFilterChanged = () => {
        if (isSyncBlocked()) return;

        const prevKey = JSON.stringify(state.activeFilterModel);
        state.activeFilterModel = buildFilterModelFromGrid(getApi().getFilterModel && getApi().getFilterModel());
        pruneSavedRestoreModelFromActive();

        if (state.shouldReapplyDropped) {
            reapplyDroppedSetFilters();
        } else if (canPersist()) {
            persistToLocalStorage();
        }

        if (prevKey !== JSON.stringify(state.activeFilterModel)) {
            state.lastPersistedKey = null;
        }
    };

    const reset = () => {
        state.savedFilterModelToRestore = null;
        state.filtersRestored = true;
        state.activeFilterModel = {};
        state.lastPersistedKey = null;
    };

    const clearAllState = () => {
        reset();
        state.filtersRestored = false;
        state.shouldReapplyDropped = false;
    };

    return {
        state,
        isSyncBlocked,
        getLocalStorageKey,
        getPreferencesToRestore,
        loadPendingFilters,
        restoreFromStorage,
        syncFromGrid,
        getEffectiveForRequest,
        buildFilterModelFromGrid,
        onFilterChanged,
        persistFromParams,
        persistToLocalStorage,
        canPersist,
        reset,
        clearAllState,
        sanitizeFilterModel,
        hasActiveFilterModel,
        isEmptyFilterEntry,
        isInvalidDateFilterEntry
    };
}

module.exports = {
    isEmptyFilterEntry,
    isInvalidDateFilterEntry,
    sanitizeFilterModel,
    hasActiveFilterModel,
    createDataGridFilterPersistence
};
